import type { MenuItem } from "../types";

export type NavBarLinkLike = {
  menuTitle?: string;
  key?: string;
  path?: string;
  resource?: string;
  show?: boolean;
};
export type NavBarLinksLike = Record<string, NavBarLinkLike[]>;

const SECTION_ALIASES: Record<string, string> = {
  dashboard: "dashboard",
  sales: "business",
  operations: "operations",
  warehousing: "warehousing",
  reports: "reports",
  settings: "settings",
  network: "network",
};

export function findByKey(list: MenuItem[], key: string | null): MenuItem | null {
  if (!key) return null;
  for (const item of list) {
    if (item.key === key) return item;
    if (item.children) {
      const f = findByKey(item.children, key);
      if (f) return f;
    }
  }
  return null;
}

export function getNode(key: string | null, roots: MenuItem[][]): MenuItem | null {
  for (const root of roots) {
    const n = findByKey(root, key);
    if (n) return n;
  }
  return null;
}

const norm = (s?: string) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");

const getAllowedForSection = (nav: NavBarLinksLike, sectionKey: string): NavBarLinkLike[] => {
  const navKey = Object.keys(SECTION_ALIASES).find((k) => SECTION_ALIASES[k] === sectionKey);
  return navKey ? nav[navKey] || [] : [];
};

const getItemPath = (item: MenuItem) => {
  const p = (item as any)?.path;
  return typeof p === "string" ? p : undefined;
};

const matchLink = (item: MenuItem, links: NavBarLinkLike[]): NavBarLinkLike | undefined => {
  const itemKey = norm(item.key);
  const itemLabel = norm(String(item.label ?? ""));
  const itemPath = norm(getItemPath(item));

  return (
    links.find((l) => norm(l.key) === itemKey) ||
    links.find((l) => norm(l.resource) === itemKey) ||
    links.find((l) => norm(l.menuTitle) === itemLabel) ||
    links.find((l) => norm(l.path) === itemPath)
  );
};

export type FilterOptions = {
  forceNetwork?: boolean;
  isMilitary?: boolean;
};

export function filterAndPatchMenu(
  menu: MenuItem[],
  nav: NavBarLinksLike,
  opts?: FilterOptions
): MenuItem[] {
  const walk = (items: MenuItem[], sectionKey?: string): MenuItem[] => {
    if (!items?.length) return [];

    const allowedLinks = sectionKey ? getAllowedForSection(nav, sectionKey) : [];

    return items.reduce<MenuItem[]>((acc, item) => {
      const isTopLevel = !sectionKey;
      const sectionForChildren = isTopLevel ? item.key : sectionKey;

      const patchedChildren = item.children ? walk(item.children, sectionForChildren) : undefined;

      const hasAnyForSection = isTopLevel ? getAllowedForSection(nav, item.key).length > 0 : false;

      const link = isTopLevel ? undefined : matchLink(item, allowedLinks);

      const isNetworkTop = isTopLevel && item.key === "network";
      const allowNetwork = Boolean(opts?.forceNetwork && isNetworkTop);

      if (item.militaryOnly && !opts?.isMilitary) {
        return acc;
      }

      const keep = isTopLevel
        ? Boolean(allowNetwork || hasAnyForSection || (patchedChildren && patchedChildren.length))
        : Boolean(link || (patchedChildren && patchedChildren.length));

      if (keep) {
        acc.push({
          ...item,
          ...(patchedChildren ? { children: patchedChildren } : { children: undefined }),
        });
      }
      return acc;
    }, []);
  };

  return walk(menu, undefined);
}
